import { useMemo, useState } from "react"
import { createFileRoute } from "@tanstack/react-router"
import { Activity, Loader2 } from "lucide-react"
import { resolveEventId, useActiveEvent } from "@/core/active-event"
import {
  ACTIVITY_CATEGORIES,
  ACTIVITY_IMPORTANCE,
  activityFeedQueryOptions,
  type ActivityFilters,
} from "@/core/activity"
import { useActivityFeed } from "@/lib/use-activity-feed"
import {
  ActivityRow,
  activityMetaFor,
} from "@/components/activity/ActivityRow"
import { cn } from "@/core/utils"

// Mount-default filters: all categories selected ⇒ `categories: undefined`,
// no importance filter. Match the component's initial useActivityFeed args so
// the prefetched first page hits the same cache key.
const DEFAULT_ACTIVITY_FILTERS: ActivityFilters = {
  categories: undefined,
  importance: undefined,
}

export const Route = createFileRoute("/$orgSlug/$eventSlug/activity")({
  loader: ({ context, params }) => {
    void resolveEventId(params.orgSlug, params.eventSlug).then((eventId) => {
      if (!eventId) return
      void context.queryClient
        .ensureInfiniteQueryData(
          activityFeedQueryOptions(eventId, DEFAULT_ACTIVITY_FILTERS),
        )
        .catch(() => undefined)
    })
  },
  component: ActivityView,
})

const IMPORTANCE_LABEL: Record<string, string> = {
  critical: "Critical",
  notable: "Notable",
  info: "Info",
}

function ActivityView() {
  const { eventId } = useActiveEvent()

  // All categories on by default; toggling a chip narrows the feed. The
  // server query takes `undefined` for "all" so the filter is a no-op until
  // the user actually deselects something.
  const [selectedCategories, setSelectedCategories] = useState<string[]>([
    ...ACTIVITY_CATEGORIES,
  ])
  const [selectedImportance, setSelectedImportance] = useState<string[]>([])

  const filters = useMemo<ActivityFilters>(() => {
    const allCats = selectedCategories.length === ACTIVITY_CATEGORIES.length
    return {
      categories: allCats
        ? undefined
        : selectedCategories.length > 0
          ? selectedCategories
          : ["__none__"],
      importance:
        selectedImportance.length > 0 ? selectedImportance : undefined,
    }
  }, [selectedCategories, selectedImportance])

  const feed = useActivityFeed(eventId, filters)

  const toggleCategory = (c: string) =>
    setSelectedCategories((prev) =>
      prev.includes(c) ? prev.filter((x) => x !== c) : [...prev, c],
    )
  const toggleImportance = (i: string) =>
    setSelectedImportance((prev) =>
      prev.includes(i) ? prev.filter((x) => x !== i) : [...prev, i],
    )

  const noCategoriesSelected = selectedCategories.length === 0

  return (
    <div className="flex h-full min-h-0 flex-col @container">
      {/* Standard canvas header (h-[52px], hairline border). Realtime/webhook
          status is shown only by the green dot on the menubar avatar, so no
          per-view "Live" badge here. */}
      <header className="flex h-[52px] shrink-0 items-center gap-2.5 border-b border-plum-200/50 px-4 dark:border-white/10">
        <div className="min-w-0">
          <h1 className="truncate text-[15px] font-semibold text-plum-900 dark:text-plum-50">
            Activity
          </h1>
        </div>
      </header>

      {/* Filter chips: categories, then importance. */}
      <div className="flex flex-wrap items-center gap-1.5 border-b border-plum-200/40 px-4 py-2.5 @[640px]:gap-2 dark:border-white/8">
        {ACTIVITY_CATEGORIES.map((c) => {
          const m = activityMetaFor(c)
          const on = selectedCategories.includes(c)
          return (
            <FilterChip
              key={c}
              on={on}
              dot={m.dot}
              chipOnClass={m.chip}
              label={m.label}
              onClick={() => toggleCategory(c)}
            />
          )
        })}

        <span className="mx-1 h-4 w-px bg-plum-200/60 dark:bg-white/10" />

        {ACTIVITY_IMPORTANCE.map((i) => {
          const on = selectedImportance.includes(i)
          return (
            <button
              key={i}
              type="button"
              data-on={on}
              onClick={() => toggleImportance(i)}
              className={cn(
                "rounded-full border px-2 py-0.5 text-[11px] font-medium transition-colors",
                "hover:bg-plum-100/60 dark:hover:bg-white/5",
                on
                  ? "border-transparent bg-plum-600 text-white dark:bg-plum-500"
                  : "border-plum-200/70 text-plum-700/60 dark:border-white/10 dark:text-plum-200/45",
              )}
              title={`Only ${IMPORTANCE_LABEL[i]}`}
            >
              {IMPORTANCE_LABEL[i]}
            </button>
          )
        })}
      </div>

      {/* Feed body */}
      <div className="scroll-thin min-h-0 flex-1 overflow-y-auto">
        {feed.isLoading ? (
          <FeedSkeleton />
        ) : feed.isError ? (
          <div className="px-4 py-12 text-center text-[12.5px] text-plum-700/60 dark:text-plum-200/45">
            Couldn&apos;t load activity.{" "}
            <button
              onClick={() => void feed.refetch()}
              className="font-semibold text-plum-700 underline dark:text-plum-200"
            >
              Retry
            </button>
          </div>
        ) : feed.items.length === 0 ? (
          <EmptyFeed noCategoriesSelected={noCategoriesSelected} />
        ) : (
          <>
            <ol className="mx-auto w-full max-w-3xl px-4 py-3 @[700px]:px-6">
              {feed.items.map((e, i) => (
                <ActivityRow
                  key={e.id}
                  event={e}
                  isLast={i === feed.items.length - 1}
                />
              ))}
            </ol>
            {feed.hasMore && (
              <div className="flex justify-center pb-5">
                <button
                  type="button"
                  disabled={feed.isFetchingNextPage}
                  onClick={() => void feed.fetchNextPage()}
                  className="flex items-center gap-1.5 rounded-lg border border-plum-200/70 px-3 py-1.5 text-[12px] font-semibold text-plum-700/75 transition hover:bg-plum-100/60 disabled:opacity-50 dark:border-white/10 dark:text-plum-200/55 dark:hover:bg-white/5"
                >
                  {feed.isFetchingNextPage && (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  )}
                  Load more
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}

function FilterChip({
  on,
  dot,
  chipOnClass,
  label,
  onClick,
}: {
  on: boolean
  dot: string
  chipOnClass: string
  label: string
  onClick: () => void
}) {
  return (
    <button
      type="button"
      data-on={on}
      onClick={onClick}
      className={cn(
        // Constant font-weight: selection is signalled by colour only, so the
        // pill never changes width when toggled.
        "flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-medium transition-colors",
        "hover:bg-plum-100/60 dark:hover:bg-white/5",
        chipOnClass,
        on
          ? "border-transparent"
          : "border-plum-200/70 text-plum-700/60 dark:border-white/10 dark:text-plum-200/45",
      )}
    >
      <span className={cn("size-1.5 rounded-full", dot)} />
      {label}
    </button>
  )
}

function EmptyFeed({ noCategoriesSelected }: { noCategoriesSelected: boolean }) {
  return (
    <div className="flex h-full items-center justify-center p-8">
      <div className="max-w-sm text-center">
        <div className="mx-auto mb-3 grid h-12 w-12 place-items-center rounded-2xl bg-plum-100/70 text-plum-700/70 dark:bg-white/5 dark:text-plum-200/55">
          <Activity className="h-5 w-5" />
        </div>
        <h2 className="text-[15px] font-semibold text-plum-900 dark:text-plum-50">
          {noCategoriesSelected ? "No categories selected" : "No activity yet"}
        </h2>
        <p className="mt-1.5 text-[13px] leading-relaxed text-plum-700/55 dark:text-plum-200/45">
          {noCategoriesSelected
            ? "Turn a category back on to see its events."
            : "As guests RSVP, send messages, and check in, it'll show up here live."}
        </p>
      </div>
    </div>
  )
}

function FeedSkeleton() {
  return (
    <div className="mx-auto w-full max-w-3xl px-4 py-3 @[700px]:px-6">
      {Array.from({ length: 8 }).map((_, i) => (
        <div key={i} className="flex gap-3 pb-3.5">
          <span className="mt-0.5 size-[25px] shrink-0 animate-pulse rounded-full bg-plum-100/70 dark:bg-white/5" />
          <div className="min-w-0 flex-1 space-y-1.5 pt-px">
            <span className="block h-3 w-3/4 animate-pulse rounded bg-plum-100/70 dark:bg-white/5" />
            <span className="block h-2.5 w-1/4 animate-pulse rounded bg-plum-100/70 dark:bg-white/5" />
          </div>
          <span className="mt-px h-2.5 w-8 shrink-0 animate-pulse rounded bg-plum-100/70 dark:bg-white/5" />
        </div>
      ))}
    </div>
  )
}
